import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Upload } from 'lucide-react';
import { toast } from 'sonner';

import { Button } from '@/marketing/components/ui/button';
import { useDebounce } from '@/marketing/hooks/useDebounce';
import { extractErrorMessage } from '@/marketing/lib/errorMessage';
import {
  DEFAULT_VOUCHER_FILTERS,
  toVoucherListParams,
  type VoucherFilterState,
} from '@/marketing/lib/voucherListParams';
import type { InfluencerVoucher, VoucherListTotals } from '@/marketing/models/voucher';
import { INFLUENCER_CAMPAIGN_QUERIES } from '@/marketing/infrastructure/influencer/campaign/utils/queries';
import { VOUCHER_KEYS } from '@/marketing/infrastructure/influencer/voucher/utils/keys';
import { CampaignPipelineType } from '@/marketing/models/campaignPipeline';
import { VOUCHER_MUTATIONS, VOUCHER_QUERIES } from '@/marketing/infrastructure/influencer/voucher/utils/queries';
import { CommissionTierCard } from './CommissionTierCard';
import type { CommissionRatesSubmit } from './VoucherCommissionDialog';
import { VoucherKpiCards } from './VoucherKpiCards';
import { VoucherFilterBar } from './VoucherFilterBar';
import { VoucherTable } from './VoucherTable';
import { VoucherPerformanceSheet } from './VoucherPerformanceSheet';
import { VoucherCommissionDialog } from './VoucherCommissionDialog';
import { GenerateVouchersDialog } from './GenerateVouchersDialog';
import { ImportVouchersDialog } from './ImportVouchersDialog';

const SEARCH_DEBOUNCE_MS = 300;

const EMPTY_TOTALS: VoucherListTotals = {
  activeCodes: 0,
  disabledCodes: 0,
  redemptions: 0,
  orderCount: 0,
  revenueTotal: '0',
  commissionTotal: '0',
  commissionAccrued: '0',
};

export function AffiliatePage() {
  const queryClient = useQueryClient();
  const [filters, setFilters] = useState<VoucherFilterState>(DEFAULT_VOUCHER_FILTERS);
  const [selectedVoucher, setSelectedVoucher] = useState<InfluencerVoucher | null>(null);
  const [commissionTarget, setCommissionTarget] = useState<InfluencerVoucher | null>(null);
  const [pendingCode, setPendingCode] = useState<string | null>(null);
  const [generateOpen, setGenerateOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);

  const debouncedSearch = useDebounce(filters.search, SEARCH_DEBOUNCE_MS);
  const listParams = useMemo(
    () => toVoucherListParams({ ...filters, search: debouncedSearch }),
    [filters, debouncedSearch],
  );
  const { data: listing, isPending, isError } = useQuery(VOUCHER_QUERIES.list(listParams));
  // Same key as the tier card's query, so the cache serves both without a second request.
  const { data: tierRates } = useQuery(VOUCHER_QUERIES.tierRates());
  const { data: campaigns } = useQuery(INFLUENCER_CAMPAIGN_QUERIES.list());
  const affiliateCampaigns = useMemo(
    () => (campaigns ?? []).filter((campaign) => campaign.pipelineType === CampaignPipelineType.Affiliate),
    [campaigns],
  );

  // Any filter change restarts paging; keeping the old offset would land the admin on an empty page.
  const changeFilters = (change: Partial<VoucherFilterState>) => {
    setFilters((current) => ({ ...current, ...change, pageIndex: change.pageIndex ?? 0 }));
  };

  const settleMutation = async (message: string) => {
    await queryClient.invalidateQueries({ queryKey: VOUCHER_KEYS.all });
    setPendingCode(null);
    toast.success(message);
  };

  const failMutation = (error: unknown, fallback: string) => {
    setPendingCode(null);
    toast.error(extractErrorMessage(error, fallback));
  };

  const statusMutation = useMutation({
    ...VOUCHER_MUTATIONS.setStatus(),
    onSuccess: (result) =>
      settleMutation(`${result.code} is now ${result.status === 'active' ? 'active' : 'disabled'}`),
    onError: (error) => failMutation(error, 'Failed to change the code status'),
  });

  const commissionMutation = useMutation({
    ...VOUCHER_MUTATIONS.setCommission(),
    onSuccess: async (result) => {
      setCommissionTarget(null);
      await settleMutation(`Commission rates saved for ${result.code}`);
    },
    onError: (error) => failMutation(error, 'Failed to change the commission rate'),
  });

  const toggleStatus = (voucher: InfluencerVoucher) => {
    setPendingCode(voucher.code);
    statusMutation.mutate({ code: voucher.code, status: voucher.status === 'active' ? 'disabled' : 'active' });
  };

  const saveCommission = (rates: CommissionRatesSubmit) => {
    if (!commissionTarget) {
      return;
    }
    setPendingCode(commissionTarget.code);
    commissionMutation.mutate({ code: commissionTarget.code, ...rates });
  };

  const total = listing?.total ?? 0;
  const pageStart = total === 0 ? 0 : filters.pageIndex * filters.pageSize + 1;
  const pageEnd = Math.min(total, (filters.pageIndex + 1) * filters.pageSize);

  return (
    <div className="tw-flex tw-h-full tw-flex-col tw-gap-4 tw-overflow-y-auto tw-p-6">
      <div className="tw-flex tw-items-start tw-gap-3">
        <div>
          <h1 className="tw-text-xl tw-font-bold">Affiliate Vouchers</h1>
          <p className="tw-mt-0.5 tw-text-sm tw-text-muted-foreground">
            Per-creator coupon codes, commission and sales tracking
          </p>
        </div>
        <div className="tw-ml-auto tw-flex tw-gap-2">
          <Button variant="outline" onClick={() => setImportOpen(true)}>
            <Upload className="tw-mr-1.5 tw-h-4 tw-w-4" />
            Import CSV
          </Button>
          <Button onClick={() => setGenerateOpen(true)}>
            <Plus className="tw-mr-1.5 tw-h-4 tw-w-4" />
            Issue codes
          </Button>
        </div>
      </div>

      <VoucherKpiCards totals={listing?.totals ?? EMPTY_TOTALS} currency={listing?.currency ?? 'USD'} />

      <CommissionTierCard />

      <VoucherFilterBar
        filters={filters}
        onChange={changeFilters}
        resultLabel={total === 0 ? 'No codes' : `${pageStart}-${pageEnd} of ${total} codes`}
        affiliateCampaigns={affiliateCampaigns}
      />

      {isPending && <div className="tw-py-16 tw-text-center tw-text-sm tw-text-muted-foreground">Loading codes…</div>}
      {isError && (
        <div className="tw-py-16 tw-text-center tw-text-sm tw-text-muted-foreground">
          Failed to load affiliate codes.
        </div>
      )}
      {!isPending && !isError && listing && (
        <>
          <VoucherTable
            vouchers={listing.codes}
            currency={listing.currency}
            tierDefaults={tierRates ?? null}
            selectedCode={selectedVoucher?.code ?? null}
            pendingCode={pendingCode}
            onSelect={setSelectedVoucher}
            onToggleStatus={toggleStatus}
            onEditCommission={setCommissionTarget}
          />
          <div className="tw-flex tw-items-center tw-justify-end tw-gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={filters.pageIndex === 0}
              onClick={() => changeFilters({ pageIndex: filters.pageIndex - 1 })}
            >
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={pageEnd >= total}
              onClick={() => changeFilters({ pageIndex: filters.pageIndex + 1 })}
            >
              Next
            </Button>
          </div>
        </>
      )}

      <VoucherPerformanceSheet voucher={selectedVoucher} onClose={() => setSelectedVoucher(null)} />
      <VoucherCommissionDialog
        voucher={commissionTarget}
        tierDefaults={tierRates ?? null}
        isPending={commissionMutation.isPending}
        onClose={() => setCommissionTarget(null)}
        onSubmit={saveCommission}
      />
      <GenerateVouchersDialog open={generateOpen} onClose={() => setGenerateOpen(false)} />
      <ImportVouchersDialog open={importOpen} onClose={() => setImportOpen(false)} />
    </div>
  );
}
